/** Client-side walkthrough frame capture via `<video>` + `<canvas>` (no downscale in v1). */

export type CaptureVideoFrameOptions = {
  jpegQuality?: number;
  fileName?: string;
};

/** Filename slug for a captured frame, e.g. `walkthrough-frame-0m14s.jpg`. */
export const formatVideoFrameCaptureFilename = (currentTimeSeconds: number): string => {
  const wholeSeconds = Math.max(0, Math.floor(currentTimeSeconds));
  const minutes = Math.floor(wholeSeconds / 60);
  const seconds = `${wholeSeconds % 60}`.padStart(2, '0');
  return `walkthrough-frame-${minutes}m${seconds}s.jpg`;
};

/**
 * Draws the video's current decoded frame into a one-shot canvas and returns a JPEG `File`.
 *
 * Memory: uses a throwaway canvas, clears dimensions in `finally`, and does not retain blobs or
 * bitmaps after the `File` is constructed. Callers should not cache the returned `File` longer
 * than needed for upload. Requires `crossOrigin="anonymous"` when the playback URL is cross-origin.
 */
export const captureVideoFrameAsJpegFile = async (
  video: HTMLVideoElement,
  options: CaptureVideoFrameOptions = {},
): Promise<File> => {
  const { jpegQuality = 1, fileName } = options;

  if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
    throw new Error('Video frame is not ready to capture');
  }

  const width = video.videoWidth;
  const height = video.videoHeight;
  if (!width || !height) {
    throw new Error('Video has no decoded frame dimensions');
  }

  const canvas = document.createElement('canvas');
  try {
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Failed to get canvas context');

    context.drawImage(video, 0, 0, width, height);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        result => result ? resolve(result) : reject(new Error('Failed to encode JPEG')),
        'image/jpeg',
        jpegQuality,
      );
    });

    return new File(
      [blob],
      fileName ?? formatVideoFrameCaptureFilename(video.currentTime),
      { type: 'image/jpeg', lastModified: Date.now() },
    );
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
};
